import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { uploadAppealFinalVerdictDecision } from "services/appealService";
import { FILE_URL } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  verdictId?: number;
  documents?: any[];
  canUpload: boolean;
  onUploaded: () => void;
};

// Upload the final appeal/cassation court-decision file AFTER the verdict has
// been created (document_type final_verdict_court_decision).
const FinalVerdictDecisionUpload: FC<Props> = ({
  verdictId,
  documents,
  canUpload,
  onUploaded,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const decisionDocs = (documents || []).filter(
    (d: any) => d?.document_type === "final_verdict_court_decision",
  );
  const hasFile = decisionDocs.length > 0;

  const handleUpload = () => {
    if (!file) {
      showTooltip(
        t("court_decision_file_required", {
          defaultValue: "Please select the court decision file.",
        }),
        "error",
      );
      return;
    }
    if (!verdictId) return;
    setLoading(true);
    uploadAppealFinalVerdictDecision(verdictId, file)
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        setFile(null);
        onUploaded();
      })
      .catch((err) =>
        showTooltip(
          err?.response?.data?.detail || errorHandlerOnForm(err),
          "error",
        ),
      )
      .finally(() => setLoading(false));
  };

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/60 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base">📄</span>
        <h5 className="text-sm font-semibold text-gray-900">
          {t("court_decision_file", { defaultValue: "Court Decision File" })}
        </h5>
      </div>

      {hasFile ? (
        <DocumentViewerModal
          documents={decisionDocs as any}
          baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.APPEAL_FINAL_VERDICT}`}
          layout="list"
          hideEmptyFile={true}
        />
      ) : canUpload ? (
        <div className="space-y-2">
          <FileUpload
            label=""
            fileTypes="*"
            info={t("court_decision_file_hint", {
              defaultValue:
                "Attach the final appeal/cassation court decision document.",
            })}
            required={false}
            onChange={(f) => setFile(f)}
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleUpload}
              disabled={loading || !file}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  {LoadingIcon}
                  {t("loading")}
                </>
              ) : (
                `⬆ ${t("upload", { defaultValue: "Upload" })}`
              )}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-400">
          {t("no_court_decision_file", {
            defaultValue: "No court decision file uploaded yet.",
          })}
        </p>
      )}
    </div>
  );
};

export default FinalVerdictDecisionUpload;
